import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const weddingLinks = [
  { name: 'Wedding Overview', href: '/weddings' },
  { name: 'Ceremony Experience', href: '/weddings/ceremony' },
  { name: 'Reception & Flow', href: '/weddings/reception' },
  { name: 'Food & Catering', href: '/weddings/food-and-catering' },
  { name: 'Investment & Minimums', href: '/weddings/investment' },
  { name: 'Spanish Revival Venue', href: '/weddings/spanish-revival-venue' },
  { name: 'Private Estate Setting', href: '/weddings/private-estate-wedding-venue' },
];

const eventLinks = [
  { name: 'Events Overview', href: '/events' },
  { name: 'Corporate Events', href: '/events/corporate' },
  { name: 'Private Celebrations', href: '/events/private-events' },
  { name: 'Quinceañeras', href: '/events/quinceaneras' },
];

const locationLinks = [
  { name: 'Where Couples Come From', href: '/where-our-couples-come-from' },
  { name: 'Pasadena Region', href: '/pasadena-wedding-venue' },
  { name: 'Orange County Region', href: '/orange-county-wedding-venue' },
  { name: 'Los Angeles Region', href: '/los-angeles-wedding-venue' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const navItemClass = "text-[11px] tracking-[0.15em] uppercase hover:text-brand-olive transition-colors text-brand-vintage font-medium whitespace-nowrap cursor-pointer flex items-center gap-1.5";

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-700 ${scrolled || mobileMenuOpen ? 'bg-brand-charcoal/95 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          
          <Link to="/" className="font-serif text-2xl md:text-3xl tracking-tighter transition-colors text-brand-vintage shrink-0 z-50 relative">
            Padua Weddings
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <Link to="/" className={navItemClass}>Home</Link>
            
            {/* Weddings Dropdown */}
            <div 
              className="relative group py-2"
              onMouseEnter={() => setActiveDropdown('weddings')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link to="/weddings" className={navItemClass}>
                Weddings
                <SafeIcon icon={FiIcons.FiChevronDown} className={`text-[12px] transition-transform duration-300 ${activeDropdown === 'weddings' ? 'rotate-180' : ''}`} />
              </Link>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 w-[280px] bg-brand-vintage shadow-2xl border border-brand-stone/40 py-4 transition-all duration-500 ${activeDropdown === 'weddings' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                {weddingLinks.map(item => (
                  <Link key={item.name} to={item.href} className="block px-8 py-3 text-[10px] tracking-[0.15em] uppercase text-brand-charcoal hover:bg-brand-aged hover:text-brand-olive transition-colors whitespace-nowrap">
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/weddings/private-tour" className={navItemClass}>Private Tour</Link>
            <a href="https://gallery.paduaweddings.com" className={navItemClass}>Gallery</a>
            <Link to="/weddings/faq" className={navItemClass}>FAQ</Link>

            {/* Events Dropdown */}
            <div 
              className="relative group py-2"
              onMouseEnter={() => setActiveDropdown('events')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link to="/events" className={navItemClass}>
                Events
                <SafeIcon icon={FiIcons.FiChevronDown} className={`text-[12px] transition-transform duration-300 ${activeDropdown === 'events' ? 'rotate-180' : ''}`} />
              </Link>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 w-[260px] bg-brand-vintage shadow-2xl border border-brand-stone/40 py-4 transition-all duration-500 ${activeDropdown === 'events' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                {eventLinks.map(item => (
                  <Link key={item.name} to={item.href} className="block px-8 py-3 text-[10px] tracking-[0.15em] uppercase text-brand-charcoal hover:bg-brand-aged hover:text-brand-olive transition-colors whitespace-nowrap">
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Locations Dropdown */}
            <div 
              className="relative group py-2"
              onMouseEnter={() => setActiveDropdown('locations')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link to="/where-our-couples-come-from" className={navItemClass}>
                Locations
                <SafeIcon icon={FiIcons.FiChevronDown} className={`text-[12px] transition-transform duration-300 ${activeDropdown === 'locations' ? 'rotate-180' : ''}`} />
              </Link>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 w-[280px] bg-brand-vintage shadow-2xl border border-brand-stone/40 py-4 transition-all duration-500 ${activeDropdown === 'locations' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                {locationLinks.map(item => (
                  <Link key={item.name} to={item.href} className="block px-8 py-3 text-[10px] tracking-[0.15em] uppercase text-brand-charcoal hover:bg-brand-aged hover:text-brand-olive transition-colors whitespace-nowrap">
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/weddings/private-tour" className="text-[11px] tracking-[0.15em] uppercase bg-brand-olive text-brand-vintage px-6 py-3 hover:bg-brand-vintage hover:text-brand-charcoal transition-all font-bold whitespace-nowrap shadow-lg">
              Inquire
            </Link>
          </div>

          {/* Mobile Right Section */}
          <div className="flex lg:hidden items-center gap-4 z-50 relative">
            <Link to="/weddings/private-tour" className="text-[10px] tracking-[0.15em] uppercase border border-brand-vintage/30 text-brand-vintage px-4 py-2 hover:bg-brand-vintage hover:text-brand-charcoal transition-all font-bold">
              Tour
            </Link>
            <button 
              className="text-brand-vintage p-1 hover:text-brand-olive transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-3xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`lg:hidden fixed inset-0 bg-brand-charcoal z-40 transition-all duration-500 ease-in-out overflow-y-auto pt-24 pb-12 ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}>
          <div className="flex flex-col px-8 space-y-6">
            <Link to="/" className="text-lg tracking-[0.2em] uppercase text-brand-vintage hover:text-brand-olive transition-all">Home</Link>
            
            <div className="space-y-4">
              <span className="text-lg tracking-[0.2em] uppercase text-brand-vintage block">Weddings</span>
              <div className="pl-4 border-l border-brand-vintage/20 flex flex-col space-y-4">
                {weddingLinks.map(link => (
                  <Link key={link.name} to={link.href} className="text-xs tracking-[0.15em] uppercase text-brand-vintage/70 hover:text-brand-olive transition-all block">{link.name}</Link>
                ))}
                <Link to="/weddings/private-tour" className="text-xs tracking-[0.15em] uppercase text-brand-vintage/70 hover:text-brand-olive transition-all block">Private Tour</Link>
                <a href="https://gallery.paduaweddings.com" onClick={() => setMobileMenuOpen(false)} className="text-xs tracking-[0.15em] uppercase text-brand-vintage/70 hover:text-brand-olive transition-all block">Gallery</a>
                <Link to="/weddings/faq" className="text-xs tracking-[0.15em] uppercase text-brand-vintage/70 hover:text-brand-olive transition-all block">FAQ</Link>
              </div>
            </div>

            <div className="space-y-4">
              <span className="text-lg tracking-[0.2em] uppercase text-brand-vintage block">Locations</span>
              <div className="pl-4 border-l border-brand-vintage/20 flex flex-col space-y-4">
                {locationLinks.map(link => (
                  <Link key={link.name} to={link.href} className="text-xs tracking-[0.15em] uppercase text-brand-vintage/70 hover:text-brand-olive transition-all block">{link.name}</Link>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <span className="text-lg tracking-[0.2em] uppercase text-brand-vintage block">Events</span>
              <div className="pl-4 border-l border-brand-vintage/20 flex flex-col space-y-4">
                {eventLinks.map(link => (
                  <Link key={link.name} to={link.href} className="text-xs tracking-[0.15em] uppercase text-brand-vintage/70 hover:text-brand-olive transition-all block">{link.name}</Link>
                ))}
              </div>
            </div>

            <div className="w-12 h-[1px] bg-brand-vintage/20 my-6" />

            <Link to="/weddings/private-tour" className="text-sm tracking-[0.2em] uppercase bg-brand-olive text-brand-vintage px-8 py-4 text-center hover:bg-brand-vintage hover:text-brand-charcoal transition-all font-bold shadow-2xl block">
              Book a Private Tour
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;